import { useState } from 'react'
import { AlertTriangle, Check, ExternalLink } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import type { Plan } from '@/api/platform'
import { openBillingPortal, startCheckout } from '@/api/billing'
import { useBilling } from '@/features/billing/useBilling'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import PageContainer from '@/components/layout/PageContainer'

function money(cents: number | null, currency: string): string {
  if (cents == null) return 'Custom'
  return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 })
    .format(cents / 100)
}

function num(n: number): string {
  return new Intl.NumberFormat().format(n)
}

function date(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
}

/** Statuses that mean the agency is subscribed — plan changes go via the Billing Portal, not Checkout. */
const LIVE_SUBSCRIPTION_STATUSES = ['trialing', 'active', 'past_due', 'unpaid']

const SUB_STATUS_STYLE: Record<string, string> = {
  active: 'bg-success/10 text-success',
  trialing: 'bg-brand-primary/10 text-brand-primary',
  past_due: 'bg-warn/10 text-warn',
  canceled: 'bg-danger/10 text-danger',
}

/** A used/allowance meter. A null limit means unlimited — no bar, just the count. */
function Meter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0
  const over = limit != null && used > limit
  return (
    <div>
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="tabular-nums text-brand-darkest">
          {num(used)}{limit != null && <span className="text-gray-400"> / {num(limit)}</span>}
          {limit == null && <span className="text-gray-400"> · unlimited</span>}
        </span>
      </div>
      {limit != null && (
        <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div className={`h-full ${over ? 'bg-danger' : 'bg-brand-primary'}`} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  )
}

export default function Billing() {
  const { data, isLoading } = useBilling()
  // Checkout / portal return a Stripe-hosted URL; redirect the browser to it.
  const checkout = useMutation({
    mutationFn: (planKey?: string) => startCheckout(planKey),
    onSuccess: (url) => window.location.assign(url),
  })
  // Which plan's "Switch" button opened the portal, so only that button shows "Opening…"
  // (the portal is one shared action, so react-query's pending flag alone can't tell them apart).
  const [portalPlan, setPortalPlan] = useState<string | null>(null)
  const portal = useMutation({
    mutationFn: () => openBillingPortal(),
    onSuccess: (url) => window.location.assign(url),
  })
  const redirecting = checkout.isPending || portal.isPending
  const actionError = checkout.error || portal.error

  if (isLoading || !data) {
    return <PageContainer width="full"><p className="text-sm text-gray-400">Loading…</p></PageContainer>
  }

  const { plan, subscription, usage, availablePlans } = data
  const emailOver = usage.emailIncluded != null && usage.emailSent > usage.emailIncluded
  const seatsOver = usage.seatLimit != null && usage.seatsUsed > usage.seatLimit
  const selfService = availablePlans.some((p) => p.purchasable)
  // With a live subscription, plan changes must go through the Billing Portal (proration) — a fresh
  // Checkout would create a second subscription. So switch the plan buttons to "open the portal".
  const subscribed = !!subscription && LIVE_SUBSCRIPTION_STATUSES.includes(subscription.status)

  return (
    <PageContainer width="full">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-brand-darkest">Billing</h1>
        <p className="text-sm text-gray-500">Your subscription and this month&apos;s usage.</p>
      </div>

      {(emailOver || seatsOver) && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2.5 text-sm text-brand-darkest">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warn" />
          <div>
            {seatsOver && <p>You&apos;re over your plan&apos;s seat limit — adding new users is blocked until you upgrade.</p>}
            {emailOver && <p>You&apos;ve used more than your plan&apos;s monthly email allowance. Extra usage may be billed as overage.</p>}
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* current plan + usage */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 self-start space-y-4">
          <div>
            <div className="text-xs text-gray-400 uppercase tracking-wide">Current plan</div>
            {plan ? (
              <div className="mt-0.5 flex items-baseline gap-2">
                <span className="text-lg font-semibold text-brand-darkest">{plan.name}</span>
                <span className="text-sm text-gray-500">{money(plan.monthlyPriceCents, plan.currency)}/mo</span>
              </div>
            ) : (
              <div className="mt-0.5 text-sm text-gray-500">No plan assigned yet</div>
            )}
            {subscription && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                <span className={`px-2 py-0.5 rounded-full ${SUB_STATUS_STYLE[subscription.status] ?? 'bg-gray-100 text-gray-500'}`}>
                  {subscription.status.replace('_', ' ')}
                </span>
                {subscription.status === 'trialing' && subscription.trialEndsAt && (
                  <span className="text-gray-500">Trial ends {date(subscription.trialEndsAt)}</span>
                )}
                {subscription.status !== 'trialing' && subscription.currentPeriodEnd && (
                  <span className="text-gray-500">
                    {subscription.status === 'canceled' ? 'Ends' : 'Renews'} {date(subscription.currentPeriodEnd)}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3 pt-1">
            <Meter label="Seats" used={usage.seatsUsed} limit={usage.seatLimit} />
            <Meter label="Emails this month" used={usage.emailSent} limit={usage.emailIncluded} />
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-gray-600">SMS this month</span>
              <span className="tabular-nums text-brand-darkest">{num(usage.smsSent)}<span className="text-gray-400"> · metered</span></span>
            </div>
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-gray-600">Dedicated SMS numbers</span>
              <span className="tabular-nums text-brand-darkest">{num(usage.dedicatedNumbers)}</span>
            </div>
          </div>

          {subscription ? (
            <button
              type="button"
              onClick={() => { setPortalPlan(null); portal.mutate() }}
              disabled={redirecting}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-gray-200 text-sm font-medium text-brand-darkest hover:bg-gray-50 disabled:opacity-60"
            >
              <ExternalLink size={14} /> {portal.isPending ? 'Opening…' : 'Manage billing'}
            </button>
          ) : (
            !selfService && (
              <p className="text-xs text-gray-400 pt-1 border-t border-gray-100">
                To change your plan, contact your ProfitPipe account manager.
              </p>
            )
          )}
        </div>

        {/* plan catalog */}
        <div className="self-start">
          {actionError && (
            <p className="mb-3 text-danger text-sm">{apiErrorMessage(actionError)}</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {availablePlans.map((p) => (
              <PlanCard
                key={p.key}
                p={p}
                current={p.key === plan?.key}
                subscribed={subscribed}
                onSubscribe={() => checkout.mutate(p.key)}
                onManage={() => { setPortalPlan(p.key); portal.mutate() }}
                busy={redirecting}
                loading={checkout.isPending && checkout.variables === p.key}
                portalOpening={portal.isPending && portalPlan === p.key}
              />
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  )
}

function PlanCard({ p, current, subscribed, onSubscribe, onManage, busy, loading, portalOpening }: {
  p: Plan
  current: boolean
  /** The agency already has a live subscription — switching plans goes through the Billing Portal. */
  subscribed: boolean
  onSubscribe: () => void
  onManage: () => void
  /** Any checkout/portal redirect is in flight — every button is disabled to prevent a double submit. */
  busy: boolean
  /** This specific plan's checkout is in flight — only its button shows "Redirecting…". */
  loading: boolean
  /** The Billing Portal redirect is in flight. */
  portalOpening: boolean
}) {
  const feature = (text: string) => (
    <li className="flex items-center gap-1.5 text-sm text-gray-600">
      <Check size={14} className="text-success shrink-0" /> {text}
    </li>
  )
  return (
    <div className={`rounded-xl border p-4 flex flex-col ${current ? 'border-brand-primary ring-1 ring-brand-primary/30' : 'border-gray-200'} bg-white`}>
      <div className="flex items-baseline justify-between">
        <span className="font-semibold text-brand-darkest">{p.name}</span>
        {current && <span className="text-xs px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary">Current</span>}
      </div>
      <div className="mt-1 text-lg font-semibold text-brand-darkest">
        {money(p.monthlyPriceCents, p.currency)}
        {p.monthlyPriceCents != null && <span className="text-sm font-normal text-gray-400">/mo</span>}
      </div>
      <ul className="mt-3 space-y-1.5">
        {feature(p.seatLimit == null ? 'Unlimited users' : `Up to ${p.seatLimit} users`)}
        {feature(p.includedEmailMonthly == null ? 'Unlimited email' : `${num(p.includedEmailMonthly)} emails / mo`)}
        {feature('SMS metered per message')}
      </ul>
      {p.purchasable && !current && (
        subscribed ? (
          // Already subscribed: send them to the Portal to switch (proration), never a 2nd Checkout.
          <button
            type="button"
            onClick={onManage}
            disabled={busy}
            className="mt-4 w-full py-2 rounded-lg border border-gray-200 text-sm font-medium text-brand-darkest hover:bg-gray-50 disabled:opacity-60"
          >
            {portalOpening ? 'Opening…' : 'Switch in billing portal'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubscribe}
            disabled={busy}
            className="mt-4 w-full py-2 rounded-lg bg-brand-primary text-white text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {loading ? 'Redirecting…' : 'Choose plan'}
          </button>
        )
      )}
    </div>
  )
}
